import {inject, injectable} from 'inversify';

import type {Nullable} from '../../../utils/utility-types';
import type {AppEventModel} from './AppEvent.model';

import {not} from '../../../utils/conditionalUtils';
import {EnvProviderService} from '../../system/env/EnvProvider.service';
import {BaseController} from '../Base/Base.controller';

@injectable()
export class AppEventController extends BaseController<AppEventModel> {
  constructor(
    @inject(EnvProviderService)
    private readonly envProviderService: EnvProviderService,
  ) {
    super();
  }

  get ATTACHMENT_EVENT_TYPES(): AppEventModel.SupportedType[] {
    const isB2b = this.envProviderService.app.isB2b;

    return [
      'conference_started',
      'conference_stopped',
      isB2b ? 'conference_stopped_automatically' : null,
      'task_done',
      isB2b ? 'coach_coverage' : null,
    ].filter(Boolean) as AppEventModel.SupportedType[];
  }

  get CONFERENCE_RELATED_EVENTS(): AppEventModel.SupportedType[] {
    return [
      'conference_started',
      'conference_stopped',
      'conference_stopped_automatically',
      'conference_client_joined',
    ];
  }

  get COURSE_RELATED_EVENTS(): AppEventModel.SupportedType[] {
    return ['course_started', 'course_stopped', 'membership_created'];
  }

  get SUPPORTED_EVENTS(): AppEventModel.SupportedType[] {
    return [...this.ATTACHMENT_EVENT_TYPES, ...this.SYSTEM_EVENT_TYPES];
  }

  get SYSTEM_EVENT_TYPES(): AppEventModel.SupportedType[] {
    const isB2b = this.envProviderService.app.isB2b;

    return [
      'membership_created',
      'course_started',
      not(isB2b) ? 'course_stopped' : null,
      isB2b ? 'conference_client_joined' : null,
    ].filter(Boolean) as AppEventModel.SupportedType[];
  }

  checkIsAttachmentEventType(type?: Nullable<AppEventModel.Props['type']>) {
    if (!type) {
      return false;
    }

    return this.ATTACHMENT_EVENT_TYPES.includes(
      type as AppEventModel.SupportedType,
    );
  }

  checkIsConferenceRelatedEventType(
    type: Nullable<AppEventModel.Props['type']>,
  ) {
    if (!type) {
      return false;
    }

    return this.CONFERENCE_RELATED_EVENTS.includes(
      type as AppEventModel.SupportedType,
    );
  }

  checkIsCourseRelatedEventType(type: Nullable<AppEventModel.Props['type']>) {
    if (!type) {
      return false;
    }

    return this.COURSE_RELATED_EVENTS.includes(
      type as AppEventModel.SupportedType,
    );
  }

  checkIsSupportedEventType(type: Nullable<AppEventModel.Props['type']>) {
    if (!type) {
      return false;
    }

    return this.SUPPORTED_EVENTS.includes(type as AppEventModel.SupportedType);
  }

  checkIsSystemEventType(type: Nullable<AppEventModel.Props['type']>) {
    if (!type) {
      return false;
    }

    return this.SYSTEM_EVENT_TYPES.includes(
      type as AppEventModel.SupportedType,
    );
  }

  getContextTargetType(eventType: AppEventModel.Props['type']) {
    switch (eventType) {
      case 'coach_coverage':
        return 'coach_history';
      case 'conference_client_joined':
      case 'conference_started':
      case 'conference_stopped':
      case 'conference_stopped_automatically':
        return 'conference';
      case 'task_done':
        return 'task';
      default:
        return 'course';
    }
  }
}
